import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Camera, CameraOff, Loader2, X } from 'lucide-react';

import { captureFromVideo } from '../lib/images';
import { Button } from './ui/button';

/**
 * Full-screen camera with an optional silhouette overlay.
 *
 * Falls back to the OS camera app (`<input capture>`) when getUserMedia is
 * unavailable or refused — an in-app camera is nicer, but a customer standing
 * in the rain should not be blocked by a permissions prompt they dismissed.
 */
const CameraCapture = ({ overlay, overlayElement, hint, onCapture, onClose, onSkip, busy }) => {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function start() {
      const attempts = [
        { video: { facingMode: { exact: 'environment' } } },
        { video: { facingMode: 'environment' } },
        { video: true }
      ];

      for (const constraints of attempts) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          if (cancelled) {
            stream.getTracks().forEach((track) => track.stop());
            return;
          }
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => setReady(true);
            await videoRef.current.play().catch(() => null);
          }
          return;
        } catch {
          /* try the next set of constraints */
        }
      }

      if (!cancelled) setBlocked(true);
    }

    start();

    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const handleShutter = useCallback(async () => {
    try {
      // A Blob, same as the file picker produces — the upload path compresses.
      onCapture(await captureFromVideo(videoRef.current));
    } catch (err) {
      console.error('Capture failed:', err);
    }
  }, [onCapture]);

  const handleFallbackFile = (event) => {
    const file = event.target.files?.[0];
    if (file) onCapture(file);
    event.target.value = '';
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black">
      <div className="flex items-center justify-between p-4 text-white">
        <p className="text-sm font-medium">{hint}</p>
        <button type="button" onClick={onClose} aria-label="Close camera" className="rounded-full p-1.5 hover:bg-white/10">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="relative flex-1 overflow-hidden">
        {blocked ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center text-white">
            <CameraOff className="h-8 w-8 opacity-70" />
            <p className="text-sm opacity-80">
              We could not open the camera in this browser. You can still take a photo with your
              camera app.
            </p>
            <Button type="button" variant="secondary" onClick={() => fileInputRef.current?.click()}>
              <Camera className="h-4 w-4" />
              Open camera app
            </Button>
          </div>
        ) : (
          <>
            <video ref={videoRef} className="h-full w-full object-cover" playsInline autoPlay muted />

            {!ready && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-white/80" />
              </div>
            )}

            {/* Nearly the full frame: the outline is a framing guide, so if the
                car fits inside it the photo is usable. A small inset that only
                covered the middle of the view had people standing far too close. */}
            {ready && overlay && (
              <img
                src={overlay}
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 h-full w-full object-contain p-2 opacity-45"
              />
            )}

            {/* Some guides are drawn rather than photographed — the registration
                ticket outline, for instance. */}
            {ready && overlayElement}
          </>
        )}
      </div>

      <div className="grid grid-cols-3 items-center p-6 pb-safe">
        <span />

        {!blocked && (
          <button
            type="button"
            onClick={handleShutter}
            disabled={!ready || busy}
            aria-label="Take photo"
            className="mx-auto flex h-18 w-18 items-center justify-center rounded-full bg-white p-1 disabled:opacity-50"
          >
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white ring-2 ring-black/20">
              {busy ? <Loader2 className="h-6 w-6 animate-spin text-black/60" /> : <Camera className="h-6 w-6 text-black/70" />}
            </span>
          </button>
        )}

        {/* Skipping a shot has to be possible without abandoning the sequence —
            not every car has damage on every panel. */}
        {onSkip && (
          <button
            type="button"
            onClick={onSkip}
            disabled={busy}
            className="justify-self-end text-sm font-medium text-white/80 hover:text-white disabled:opacity-50"
          >
            Skip
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFallbackFile}
      />
    </div>
  );
};

export default CameraCapture;
